import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Button,
  TableCell,
  TableHead,
  Table,
  TableRow,
  TableContainer,
  TableBody,
  Typography,
  Stack,
  TableSortLabel,
  Chip,
  Grid,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Skeleton,
} from '@mui/material'
import { ChevronLeft, ExpandMore } from '@mui/icons-material'
import axios from 'axios'
import dayjs from 'dayjs'
import './PurchaseOrderDetails.css'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import ErrorScreen from '../Misc/ErrorScreen'
import { purchaseOrderLineType, purchaseOrderType } from '../types'
import { Expandable } from '../helper'

type purchaseOrderLineDetailType = purchaseOrderLineType & {
  item_name: string
}

type RelatedItemType = {
  type: string
  txn_id: string
  ref_number: string | null
  txn_date: string | null
  due_date: string | null
  amount: number | null
  status: boolean | null
}

interface InfoRowProps {
  label: string
  value: string | number | null | undefined
  isNumeric?: boolean
}

const InfoRow: React.FC<InfoRowProps & { loading?: boolean }> = ({ label, value, isNumeric, loading }) => (
  <Box className="purchase-order-detail-header-grid">
    <Typography variant="body2" color="text.secondary" className="purchase-order-detail-header-label">
      {loading ? <Skeleton width={100} /> : `${label}:`}
    </Typography>
    <Typography variant="body1" className="purchase-order-detail-header-value">
      {loading ? (
        <Skeleton width={120} />
      ) : isNumeric && typeof value === 'number' ? (
        value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
      ) : typeof value === 'string' && (label.includes('Date') || label.includes('date')) ? (
        dayjs.utc(value).format('MM/DD/YYYY')
      ) : (
        value ?? '-'
      )}
    </Typography>
  </Box>
)

function PurchaseOrderDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [purchaseOrder, setPurchaseOrder] = useState<purchaseOrderType | null>(null)
  const [purchaseOrderLines, setPurchaseOrderLines] = useState<purchaseOrderLineDetailType[]>([])
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<string>('line_numerical_order')
  const [relatedItems, setRelatedItems] = useState<RelatedItemType[]>([])
  const [stickyHeader, setStickyHeader] = useState(true)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    if (id) {
      setLoading(true)

      axios
        .get(`/vendor/${id}`)
        .then(res => {
          setPurchaseOrder(res.data.purchaseOrder)
          setPurchaseOrderLines(res.data.purchaseOrderLines || [])
          setRelatedItems(res.data.relatedItems || [])
          setLoadError(false)
        })
        .catch(err => {
          console.error(err)
          setLoadError(true)
        })
        .finally(() => setLoading(false))
    }
  }, [id])

  useEffect(() => {
    document.title = `Purchase Order ${purchaseOrder?.ref_number ? `| ${purchaseOrder.ref_number}` : ''} | Salestrack`
  }, [purchaseOrder])

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const sortedLines = React.useMemo(() => {
    return [...purchaseOrderLines].sort((a, b) => {
      const aValue = a[orderBy as keyof purchaseOrderLineType]
      const bValue = b[orderBy as keyof purchaseOrderLineType]

      if (!aValue && !bValue) return 0
      if (!aValue) return 1
      if (!bValue) return -1

      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      return order === 'asc' ? comparison : -comparison
    })
  }, [purchaseOrderLines, order, orderBy])

  return (
    <div className="purchase-order-detail">
      {loadError ? (
        <ErrorScreen
          errorText="We couldn't load this purchase order's details. Please try again or contact support if the error persists."
          title="Purchase Order Load Failed"
        />
      ) : (
        <>
          <div className="purchase-order-detail-button-container">
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                size="large"
                startIcon={<ChevronLeft />}
                variant="contained"
                loading={loading}
                onClick={e => {
                  e.preventDefault()
                  navigate(-1)
                }}
              >
                Return
              </Button>
              {loading ? (
                <Skeleton width={220} height={32} />
              ) : (
                <Chip label={`Purchase Order #: ${purchaseOrder?.ref_number}`} />
              )}
            </Stack>
          </div>

          <div className="purchase-order-detail-data-container">
            <div className="purchase-order-detail-header-div">
              <Grid component="div" container className="purchase-order-detail-header-grid-container">
                {/* First Column */}
                <Box className="purchase-order-detail-header-column">
                  <InfoRow label="Sales Order No" value={purchaseOrder?.sales_order_ref_number} loading={loading} />
                  <InfoRow label="Customer PO" value={purchaseOrder?.customer_po_number} loading={loading} />
                  <InfoRow label="Total Amount" value={purchaseOrder?.total_amount} isNumeric loading={loading} />
                  <InfoRow label="Phone Number" value={purchaseOrder?.vendor_phone} loading={loading} />
                  <InfoRow label="Vendor Name" value={purchaseOrder?.vendor_name} loading={loading} />
                </Box>

                {/* Second Column */}
                <Box className="purchase-order-detail-header-column">
                  <InfoRow
                    label="Transaction Date"
                    value={dayjs(purchaseOrder?.txn_date).format('MM/DD/YYYY')}
                    loading={loading}
                  />
                  <InfoRow
                    label="Due Date"
                    value={dayjs(purchaseOrder?.due_date).format('MM/DD/YYYY')}
                    loading={loading}
                  />
                  <InfoRow label="Total Sales Tax" value={purchaseOrder?.total_sales_tax} isNumeric loading={loading} />
                  <InfoRow label="ZIP Code" value={purchaseOrder?.ship_address_zip} loading={loading} />
                  <InfoRow
                    label="Ship Address Attention"
                    value={purchaseOrder?.ship_address_attention}
                    loading={loading}
                  />
                </Box>

                {/* Third Column */}
                <Box className="purchase-order-detail-header-column">
                  <InfoRow
                    label="Ship Address Addressee"
                    value={purchaseOrder?.ship_address_addressee}
                    loading={loading}
                  />
                  <InfoRow label="Ship Address 1" value={purchaseOrder?.ship_address_addr1} loading={loading} />
                  <InfoRow label="Ship Address 2" value={purchaseOrder?.ship_address_addr2} loading={loading} />
                  <InfoRow label="Ship Address City" value={purchaseOrder?.ship_address_city} loading={loading} />
                  <InfoRow label="Ship Address State" value={purchaseOrder?.ship_address_state} loading={loading} />
                </Box>
              </Grid>
            </div>

            <Accordion
              defaultExpanded
              className="purchase-order-lines-accordion"
              slotProps={{
                transition: {
                  onEntered: () => {
                    setStickyHeader(true)
                  },
                  onExit: () => {
                    setStickyHeader(false)
                  },
                },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                {loading ? (
                  <Skeleton width={220} height={32} />
                ) : (
                  <Typography variant="h6">Purchase Order Line Items</Typography>
                )}
              </AccordionSummary>
              <AccordionDetails className="purchase-order-lines">
                <TableContainer>
                  <Table stickyHeader={stickyHeader} color="primary">
                    <TableHead>
                      {loading ? (
                        <TableRow>
                          {Array.from({ length: 7 }).map((_, idx) => (
                            <TableCell key={idx}>
                              <Skeleton className="skeleton-header" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ) : (
                        <TableRow>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'line_numerical_order'}
                              direction={orderBy === 'line_numerical_order' ? order : 'asc'}
                              onClick={() => handleRequestSort('line_numerical_order')}
                            >
                              Line Number
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'item_name'}
                              direction={orderBy === 'item_name' ? order : 'asc'}
                              onClick={() => handleRequestSort('item_name')}
                            >
                              Item Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'description'}
                              direction={orderBy === 'description' ? order : 'asc'}
                              onClick={() => handleRequestSort('description')}
                            >
                              Description
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            <TableSortLabel
                              active={orderBy === 'quantity'}
                              direction={orderBy === 'quantity' ? order : 'asc'}
                              onClick={() => handleRequestSort('quantity')}
                            >
                              Quantity
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            <TableSortLabel
                              active={orderBy === 'rate'}
                              direction={orderBy === 'rate' ? order : 'asc'}
                              onClick={() => handleRequestSort('rate')}
                            >
                              Rate
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            <TableSortLabel
                              active={orderBy === 'amount'}
                              direction={orderBy === 'amount' ? order : 'asc'}
                              onClick={() => handleRequestSort('amount')}
                            >
                              Amount
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            <TableSortLabel
                              active={orderBy === 'tax_amount'}
                              direction={orderBy === 'tax_amount' ? order : 'asc'}
                              onClick={() => handleRequestSort('tax_amount')}
                            >
                              Tax Amount
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableHead>
                    <TableBody>
                      {loading
                        ? Array.from({ length: 5 }).map((_, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Skeleton width={40} />
                              </TableCell>
                              <TableCell>
                                <Skeleton width={120} />
                              </TableCell>
                              <TableCell>
                                <Skeleton width={200} />
                              </TableCell>
                              <TableCell align="right">
                                <Skeleton width={60} />
                              </TableCell>
                              <TableCell align="right">
                                <Skeleton width={80} />
                              </TableCell>
                              <TableCell align="right">
                                <Skeleton width={100} />
                              </TableCell>
                              <TableCell align="right">
                                <Skeleton width={100} />
                              </TableCell>
                            </TableRow>
                          ))
                        : sortedLines.map(line => (
                            <TableRow key={line.purchase_order_line_txn_id}>
                              <TableCell>{line.line_numerical_order || '-'}</TableCell>
                              <TableCell>{line.item_name || '-'}</TableCell>
                              <TableCell>
                                <Expandable text={line.item_desc || '-'} maxHeight={5} />
                              </TableCell>
                              <TableCell align="right">{line.quantity || '-'}</TableCell>
                              <TableCell align="right">
                                {line.rate?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) || '-'}
                              </TableCell>
                              <TableCell align="right">
                                {line.amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) || '-'}
                              </TableCell>
                              <TableCell align="right">
                                {line.tax_amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) ||
                                  '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>

            <TableContainer className="purchase-order-detail-table-container">
              <div className="purchase-order-detail-table-header">
                {loading ? <Skeleton width={180} height={32} /> : <Typography variant="h6">Related Items</Typography>}
              </div>
              <Table className="purchase-order-detail-table" stickyHeader>
                <TableHead>
                  {loading ? (
                    <TableRow>
                      {Array.from({ length: 6 }).map((_, idx) => (
                        <TableCell key={idx}>
                          <Skeleton className="skeleton-header" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell className="purchase-order-detail-cell purchase-order-detail-header-cell-text purchase-order-detail-column-number">
                        <Typography className="purchase-order-detail-cell-header-text">Type</Typography>
                      </TableCell>
                      <TableCell className="purchase-order-detail-cell purchase-order-detail-header-cell-numeric purchase-order-detail-column-number">
                        <Typography className="purchase-order-detail-cell-header-text">Reference Number</Typography>
                      </TableCell>
                      <TableCell className="purchase-order-detail-cell purchase-order-detail-header-cell-numeric purchase-order-detail-column-number">
                        <Typography className="purchase-order-detail-cell-header-text">Transaction Date</Typography>
                      </TableCell>
                      <TableCell className="purchase-order-detail-cell purchase-order-detail-header-cell-numeric purchase-order-detail-column-number">
                        <Typography className="purchase-order-detail-cell-header-text">Due Date</Typography>
                      </TableCell>
                      <TableCell className="purchase-order-detail-cell purchase-order-detail-header-cell-numeric purchase-order-detail-column-number">
                        <Typography className="purchase-order-detail-cell-header-text">Amount</Typography>
                      </TableCell>
                      <TableCell className="purchase-order-detail-cell purchase-order-detail-header-cell-text purchase-order-detail-column-number">
                        <Typography className="purchase-order-detail-cell-header-text">Status</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableHead>
                <TableBody>
                  {loading
                    ? Array.from({ length: 3 }).map((_, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Skeleton width={120} />
                          </TableCell>
                          <TableCell align="right">
                            <Skeleton width={100} />
                          </TableCell>
                          <TableCell align="right">
                            <Skeleton width={100} />
                          </TableCell>
                          <TableCell align="right">
                            <Skeleton width={100} />
                          </TableCell>
                          <TableCell align="right">
                            <Skeleton width={100} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={80} />
                          </TableCell>
                        </TableRow>
                      ))
                    : relatedItems.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="purchase-order-detail-cell purchase-order-detail-data-cell-text purchase-order-detail-column-number">
                            {item.type || '-'}
                          </TableCell>
                          <TableCell className="purchase-order-detail-cell purchase-order-detail-data-cell-numeric purchase-order-detail-column-number">
                            {item.ref_number ? (
                              <Link
                                component={RouterLink}
                                to={`/${item.type.toLowerCase().replace(/\s+/g, '')}/${item.txn_id}`}
                              >
                                {item.ref_number}
                              </Link>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell className="purchase-order-detail-cell purchase-order-detail-data-cell-numeric purchase-order-detail-column-number">
                            {item.txn_date ? dayjs.utc(item.txn_date).format('MM/DD/YYYY') : '-'}
                          </TableCell>
                          <TableCell className="purchase-order-detail-cell purchase-order-detail-data-cell-numeric purchase-order-detail-column-number">
                            {item.due_date ? dayjs.utc(item.due_date).format('MM/DD/YYYY') : '-'}
                          </TableCell>
                          <TableCell className="purchase-order-detail-cell purchase-order-detail-data-cell-numeric purchase-order-detail-column-number">
                            {item.amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) || '-'}
                          </TableCell>
                          <TableCell className="purchase-order-detail-cell purchase-order-detail-data-cell-text purchase-order-detail-column-number">
                            {item.status === true ? 'Invoiced' : item.status === false ? 'Not Invoiced' : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </>
      )}
    </div>
  )
}

export default PurchaseOrderDetails
